// A member's profile (plan §10, §13): per-speed ratings and a head-to-head
// record against the signed-in viewer.

import {
  api,
  ApiError,
  type PlayerProfile,
} from "../api.ts";
import { esc, h, setMsg } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { currentUser } from "../session.ts";
import { topNav } from "../ui/topnav.ts";
import { quickChallenge, savePrefs } from "../ui/quickChallenge.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { t } from "../i18n/index.ts";

const SPEED_LABELS: [string, string][] = [
  ["blitz", "chess.blitz"],
  ["rapid", "chess.rapid"],
];

export async function playerScreen(params: Params): Promise<HTMLElement> {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }
  const name = params.name ?? "";

  let profile: PlayerProfile;
  try {
    profile = await api.player(name);
  } catch (err) {
    return h(`
      <main class="card">
        <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
        <p class="msg bad">${esc(err instanceof ApiError ? err.message : t("player.loadFailed"))}</p>
        <p><a class="btn ghost" data-link href="/players">${t("player.players")}</a></p>
      </main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <h2>${esc(profile.username)}</h2>
        <div class="rating-cards"></div>
      </section>
    </main>
  `);
  el.prepend(topNav(null));

  const cards = el.querySelector<HTMLElement>(".rating-cards")!;
  for (const [speed, labelKey] of SPEED_LABELS) {
    const r = profile.ratings[speed];
    const label = t(labelKey);
    const card = document.createElement("div");
    card.className = "rating-card";
    card.innerHTML = r
      ? `<span class="rc-label">${esc(label)}</span><span class="rc-val">${r.rating}${r.provisional ? "?" : ""}</span>`
      : `<span class="rc-label">${esc(label)}</span><span class="rc-val muted">—</span>`;
    cards.append(card);
  }

  // Challenge this member to a game directly (UC-7) — only when the group gate
  // allows it; otherwise explain why the option isn't offered.
  if (!profile.is_self) {
    el.append(profile.can_challenge ? challengePanel(profile.username) : cannotChallengePanel());
  }

  // Head-to-head vs the viewer (server omits it for a self-lookup).
  if (!profile.is_self) {
    const { wins, losses, draws } = profile.h2h;
    const total = wins + losses + draws;
    const panel = h(`
      <section class="panel">
        <h2>${t("player.h2h")}</h2>
        ${
          total === 0
            ? `<p class="fine">${t("player.h2hEmpty", { who: esc(profile.username) })}</p>`
            : `<div class="h2h">
                 <div class="h2h-cell win"><strong>${wins}</strong><span>${t("player.youWon")}</span></div>
                 <div class="h2h-cell draw"><strong>${draws}</strong><span>${t("player.drawn")}</span></div>
                 <div class="h2h-cell loss"><strong>${losses}</strong><span>${t("player.youLost")}</span></div>
               </div>`
        }
      </section>`);
    el.append(panel);
  } else {
    // Own profile → account settings link private settings (UC-2).
    const link = document.createElement("a"); link.href = "/settings"; link.dataset.link = ""; link.textContent = t("settings.title"); el.append(link);
  }

  return el;
}

/** Challenge another member to a game directly (UC-7). On success the challenger
 *  goes to the waiting room; the target is pinged over the channel ladder. Uses
 *  the shared quick-challenge control (plan §8) and remembers the settings. */
function challengePanel(opponent: string): HTMLElement {
  const panel = h(`
    <section class="panel">
      <h2>${t("player.challengeName", { who: esc(opponent) })}</h2>
      <form id="challenge" novalidate>
        <div class="quick-slot"></div>
        <button class="btn primary" type="submit">${t("player.challengeName", { who: esc(opponent) })}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
    </section>`);

  const form = panel.querySelector<HTMLFormElement>("#challenge")!;
  const msg = form.querySelector(".msg") as HTMLElement;
  const button = form.querySelector("button") as HTMLButtonElement;
  const qc = quickChallenge();
  panel.querySelector<HTMLElement>(".quick-slot")!.append(qc.element);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const prefs = qc.read();
    button.disabled = true;
    setMsg(msg, t("player.sendingChal"));
    try {
      const res = await api.challengeMember(opponent, prefs.tc, prefs.color, prefs.rated);
      savePrefs(prefs);
      navigate(`/wait/${res.token}`);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("player.cantChallenge"), "bad");
      button.disabled = false;
    }
  });

  return panel;
}

/** Shown instead of the challenge form when the viewer isn't in any group that
 *  includes this member (the group gate — plan §groups). */
function cannotChallengePanel(): HTMLElement {
  return h(`
    <section class="panel">
      <p class="fine">${t("player.cantReach")}</p>
    </section>`);
}
